// Pure, dependency-free quiet-hours arithmetic so it can be unit tested
// outside Deno and shared by the scheduler, the worker, and the wizard
// preview. Twin of the SQL function `sms_next_allowed_send_time` in
// 20260910000000_sms_marketing_settings.sql — keep both in sync.

/** The local hour (0-23) of `at` in the given IANA timezone. */
export function localHour(at: Date, tz: string): number {
  const part = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    hourCycle: "h23",
  })
    .formatToParts(at)
    .find((p) => p.type === "hour")?.value;
  return part ? parseInt(part, 10) % 24 : 0;
}

/** The local calendar date (YYYY-MM-DD) of `at` in the given IANA timezone. */
export function localDate(at: Date, tz: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/** Whether `at` falls within [startHour, endHour) local time (end exclusive). */
export function isWithinSendWindow(at: Date, tz: string, startHour: number, endHour: number): boolean {
  const h = localHour(at, tz);
  return h >= startHour && h < endHour;
}

export function addDaysToDateString(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

/** UTC instant of local midnight on `dateStr` (YYYY-MM-DD) in `tz`. */
export function localMidnightUTC(dateStr: string, tz: string): Date {
  const guess = new Date(`${dateStr}T00:00:00Z`);
  const longOffset = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" })
    .formatToParts(guess)
    .find((p) => p.type === "timeZoneName")?.value ?? "GMT+00:00";
  const offset = longOffset.replace("GMT", "") || "+00:00";
  return new Date(`${dateStr}T00:00:00${offset}`);
}

/**
 * The next instant at or after `at` that falls within [startHour, endHour)
 * local time. Already-inside instants pass through unchanged. Otherwise this
 * walks forward from local midnight of the target local date one real UTC
 * hour at a time, checking the local hour via Intl each step — rather than
 * doing calendar arithmetic on the offset — so a DST transition inside the
 * search window still lands on the correct wall-clock hour.
 */
export function nextAllowedSendTime(at: Date, tz: string, startHour: number, endHour: number): Date {
  if (isWithinSendWindow(at, tz, startHour, endHour)) return at;

  const h = localHour(at, tz);
  const dateStr = localDate(at, tz);
  const targetDateStr = h < startHour ? dateStr : addDaysToDateString(dateStr, 1);

  let candidate = localMidnightUTC(targetDateStr, tz);
  for (let i = 0; i < 30; i++) {
    if (localHour(candidate, tz) === startHour) return candidate;
    candidate = new Date(candidate.getTime() + 60 * 60 * 1000);
  }
  return candidate;
}
